import React, { useState, useEffect } from "react"
import api from "../services/api"
import { Helmet } from "react-helmet"
import { Trash, EditIcon } from "lucide-react"
import Skeleton from "../modules/skeleton/Skeleton"
import Swal from "sweetalert2"

export default function Rooms() {
  const [errors, setErrors] = useState({})
  const [rooms, setRooms] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [selectedRoom, setSelectedRoom] = useState(null)
  const [roomToDelete, setRoomToDelete] = useState(null)
  const [isEditModal, setIsEditModal] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [isServerOnline, setIsServerOnline] = useState(true)
  const [errorMsg, setErrorMsg] = useState("")
  const [selectedRooms, setSelectedRooms] = useState([])

  const MAX_ROOMS = 6

  const [formData, setFormData] = useState({
    roomNumber: "",
    monthlyAmortization: "",
    roomType: "",
  })

  const emptyForm = {
    roomNumber: "",
    monthlyAmortization: "",
    roomType: "",
  }

  // Fetch rooms from the server
  const fetchRooms = async () => {
    setLoading(true)
    setErrorMsg("")

    try {
      const res = await api.get(`/api/rooms`)

      setRooms(res.data)
      setIsServerOnline(true)
    } catch (err) {
      setIsServerOnline(false)
      setRooms([])
      setErrorMsg("The server is currently offline. Please refresh the page.")
      console.error("Failed to fetch rooms", err.response || err)
    } finally {
      setLoading(false)
    }
  }

  // Validate the form inputs
  const validateForm = () => {
    const newErrors = {}

    // Room Number
    if (!formData.roomNumber.trim()) {
      newErrors.roomNumber = "Room number is required field"
    }

    // Monthly Ammortation
    if (!formData.monthlyAmortization.trim()) {
      newErrors.monthlyAmortization = "Monthly amortation is required field"
    }

    // Monthly Ammortation
    if (!formData.roomType.trim()) {
      newErrors.roomType = "Room type is required field"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))

    setErrors((prev) => {
      if (!prev[name]) return prev
      const { [name]: _, ...rest } = prev
      return rest
    })
  }

  // Handle form submission to add a room
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (rooms.length >= 6) {
      Swal.fire({
        icon: "warning",
        title: "Limit reached",
        text: "Maximum of 6 rooms only.",
      })
      return
    }

    if (!validateForm()) return

    try {
      await api.post(`/api/rooms`, formData)

      Swal.fire({
        icon: "success",
        text: "Add successfully.",
        confirmButtonColor: "#f97316",
        timer: 1200,
        showConfirmButton: false,
      })

      setIsAddModalOpen(false)
      setFormData(emptyForm)
      setErrors({})
      fetchRooms()
    } catch (err) {
      if (api.isAxiosError(err) && !err.response) {
        Swal.fire({
          icon: "error",
          title: "Backend is offline",
          text: "Cannot connect to http://localhost:5000. Start the server and try again.",
        })
        return
      }

      Swal.fire({
        icon: "error",
        title: "Update failed",
        text:
          err.response?.data?.message ||
          "Something went wrong while saving changes.",
      })
    }
  }

  // Open edit modal for a specific room
  const openEditModal = (room) => {
    setSelectedRoom(room)
    setErrors({})
    setFormData({
      roomNumber: room.roomNumber || "",
      monthlyAmortization: room.monthlyAmortization || "",
      roomType: room.roomType || "",
    })
    setIsEditModal(true)
  }

  // Handle room update
  const handleUpdate = async (e) => {
    e.preventDefault()

    if (!validateForm()) return

    try {
      const res = await api.put(`/api/rooms/${selectedRoom._id}`, formData)

      setIsEditModal(false)

      await Swal.fire({
        icon: "success",
        title: "Updated!",
        text: "Room details have been saved.",
        confirmButtonColor: "#f97316",
        timer: 1500,
        showConfirmButton: false,
      })

      setRooms((prev) =>
        prev.map((r) => (r._id === selectedRoom._id ? res.data : r))
      )

      setIsEditModal(false)
      setSelectedRoom(null)
      fetchRooms()
      setErrors({})
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Update failed",
        text: "Something went wrong while saving changes.",
      })
    }
  }

  // Confirm deletion for a single room
  const confirmDelete = async () => {
    if (selectedRooms.length === 0 && !roomToDelete) return

    try {
      // Check if deleting single room or multiple rooms
      const deleteData =
        selectedRooms.length > 0 ? selectedRooms : [roomToDelete._id]

      await api.delete(`/api/rooms`, {
        data: { ids: deleteData },
      })

      setRooms((prev) => prev.filter((r) => !deleteData.includes(r._id)))

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        confirmButtonColor: "#d1001f",
        timer: 1500,
        showConfirmButton: false,
      })
    } catch (err) {
      console.error(err)
      alert("Failed to delete room(s)")
    } finally {
      setSelectedRooms([]) // Reset selection after deletion
      setRoomToDelete(null) // Reset the room to delete
      setIsDeleteModalOpen(false)
    }
  }

  // Handle checkbox selection for rooms
  const handleSelectRoom = (roomId) => {
    setSelectedRooms((prevSelectedRooms) =>
      prevSelectedRooms.includes(roomId)
        ? prevSelectedRooms.filter((id) => id !== roomId)
        : [...prevSelectedRooms, roomId]
    )
  }

  // Fetch rooms on component mount
  useEffect(() => {
    fetchRooms()
  }, [])

  return (
    <>
      <Helmet>
        <title>Rooms - Stay Suite</title>
      </Helmet>
      <div className="p-6 w-full max-w-300 mx-auto">
        {loading ? (
          <Skeleton />
        ) : (
          <>
            <h1 className="text-2xl font-bold mb-2">Rooms</h1>
            <p className="pb-5 text-gray-600 italic text-balance text-sm">
              Fill in the details below to manage room information.
            </p>
            <div className="flex justify-end mb-8 mr-2">
              <div>
                <button
                  onClick={() => {
                    if (rooms.length >= MAX_ROOMS) {
                      Swal.fire({
                        icon: "warning",
                        title: "Room limit reached",
                        text: "You can only add up to 6 rooms.",
                        confirmButtonColor: "#f97316",
                      })
                      return
                    }

                    setFormData(emptyForm)
                    setErrors({})
                    setIsAddModalOpen(true)
                  }}
                  disabled={rooms.length >= MAX_ROOMS}
                  className={`h-10 rounded-lg px-5 text-sm font-semibold text-white shadow-sm
    ${
      rooms.length >= MAX_ROOMS
        ? "bg-gray-400 cursor-not-allowed"
        : "bg-orange-500 hover:bg-orange-600"
    }`}
                >
                  + Add Room
                </button>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
              <div className="overflow-x-auto">
                <table className="min-w-160 w-full border-collapse">
                  <thead className="sticky top-0 bg-gray-50 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 overflow-hidden ellipsis text-nowrap">
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedRooms(rooms.map((room) => room._id))
                            } else {
                              setSelectedRooms([])
                            }
                          }}
                          checked={selectedRooms.length === rooms.length}
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 overflow-hidden ellipsis text-nowrap">
                        Room Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 overflow-hidden ellipsis text-nowrap">
                        Room Number
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 overflow-hidden ellipsis text-nowrap">
                        Monthly Ammortation
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 overflow-hidden ellipsis text-nowrap">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-sm text-gray-700">
                    {rooms.length === 0 ? (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-4 py-10 text-center text-gray-500 italic"
                        >
                          No data found
                        </td>
                      </tr>
                    ) : (
                      rooms.map((room, index) => (
                        <tr
                          key={room._id}
                          className={
                            index % 2 === 0 ? "bg-white" : "bg-gray-50"
                          }
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedRooms.includes(room._id)}
                              onChange={() => handleSelectRoom(room._id)}
                            />
                          </td>
                          <td className="px-4 py-3">
                            {room.roomType || "N/A"}
                          </td>
                          <td className="px-4 py-3">{room.roomNumber}</td>
                          <td className="px-4 py-3">
                            {room.monthlyAmortization}
                          </td>
                          <td className="px-4 py-3 flex gap-2">
                            <button
                              onClick={() => openEditModal(room)}
                              className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition"
                            >
                              <EditIcon size={18} />
                            </button>
                            <button
                              onClick={() => {
                                setRoomToDelete(room)
                                setIsDeleteModalOpen(true)
                              }}
                              className="p-2 rounded-lg text-red-500 hover:bg-red-200 transition"
                            >
                              <Trash size={18} />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Multiple Deletion Button */}
            {selectedRooms.length > 0 && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg"
                >
                  Delete Selected ({selectedRooms.length})
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Room Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 animate-fadeIn">
            <h2 className="text-xl font-semibold text-gray-800 mb-1">
              Add Room
            </h2>
            <p className="text-sm text-gray-500 mb-5">Fill in room details</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Room Number */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Room Number
                </label>
                <input
                  type="text"
                  name="roomNumber"
                  value={formData.roomNumber}
                  onChange={handleChange}
                  placeholder="Please provide room number"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                  maxLength={4}
                />
                {errors.roomNumber && (
                  <p className="text-xs text-red-500">{errors.roomNumber}</p>
                )}
              </div>

              {/* Monthly Ammortation */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Monthly Ammortation
                </label>
                <input
                  type="text"
                  name="monthlyAmortization"
                  value={formData.monthlyAmortization}
                  onChange={handleChange}
                  placeholder="Please provide ammortation"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                />
                {errors.monthlyAmortization && (
                  <p className="text-xs text-red-500">
                    {errors.monthlyAmortization}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Room Type
                </label>

                <select
                  name="roomType"
                  value={formData.roomType}
                  onChange={handleChange}
                  className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 border-gray-300"
                >
                  <option value="" disabled>
                    Select room type
                  </option>
                  <option value="1BR">1BR</option>
                </select>

                {errors.roomType && (
                  <p className="text-xs text-red-500">{errors.roomType}</p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddModalOpen(false)
                    setFormData(emptyForm)
                    setErrors({})
                  }}
                  className="px-4 py-2 rounded-lg border text-sm text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg text-sm font-semibold bg-orange-600 text-white hover:bg-orange-700 transition"
                >
                  Add Room
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-sm rounded-xl shadow-2xl p-6 animate-fadeIn">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Delete Room
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete room{" "}
              <span className="font-semibold">{roomToDelete?.roomNumber}</span>?
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false)
                  setRoomToDelete(null)
                }}
                className="px-4 py-2 rounded-lg border text-sm text-gray-700 hover:bg-gray-100 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {isEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 animate-fadeIn">
            <h2 className="text-xl font-semibold text-gray-800">Edit Room</h2>
            <p className="text-sm text-gray-500 mb-4">Update room details</p>

            <form onSubmit={handleUpdate} className="space-y-4">
              {/* Room Number */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Room Number
                </label>
                <input
                  type="text"
                  name="roomNumber"
                  value={formData.roomNumber}
                  onChange={handleChange}
                  className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 border-gray-300"
                />
              </div>

              {/* Monthly Ammortation */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Monthly Ammortation
                </label>
                <input
                  type="text"
                  name="monthlyAmortization"
                  value={formData.monthlyAmortization}
                  onChange={handleChange}
                  className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 border-gray-300"
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Room Type
                </label>
                <select
                  name="roomType"
                  value={formData.roomType}
                  onChange={handleChange}
                  className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 border-gray-300"
                >
                  <option value="" disabled>
                    Select room type
                  </option>
                  <option value="1BR">1BR</option>
                </select>

                {errors.roomType && (
                  <p className="text-xs text-red-500">{errors.roomType}</p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditModal(false)
                    setSelectedRoom(null)
                  }}
                  className="px-4 py-2 rounded-lg border text-sm"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg text-sm font-semibold bg-orange-600 text-white"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
